import { ComponentPropsWithoutRef, ElementRef, forwardRef } from "react";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Tooltip,
} from "@altimateai/lego";
import { DAYS_OF_MONTH } from "@/pages/subscriptions/components/wizard/steps/customizeSubscription/constants";

const DAY_OF_MONTH_OPTIONS = DAYS_OF_MONTH.map(day => String(day));

// Days that don't occur in every month; the schedule is skipped in shorter months.
const SHORT_MONTH_DAYS = new Set(["29", "30", "31"]);

const SHORT_MONTH_TOOLTIP =
  "Triggers only if this date occurs in the month — otherwise it is skipped silently.";

// Lifts the tooltip above the open Select panel (`al-z-[100]`); the lego
// default of `al-z-50` renders it behind the dropdown. Set inline because
// `twMerge` can't dedupe the `al-` prefixed z-index utility via className.
const TOOLTIP_Z_INDEX = 101;

type SelectTriggerProps = ComponentPropsWithoutRef<typeof SelectTrigger>;

interface DayOfMonthSelectProps extends Pick<
  SelectTriggerProps,
  "id" | "className" | "aria-invalid" | "aria-describedby"
> {
  /** Selected day of month, "1"-"31". */
  value: string;
  onValueChange: (value: string) => void;
}

/**
 * Day-of-month picker shared by the Studio schedule dialog
 * (`pages/playground/ScheduleDayPicker.tsx`) and the Subscriptions wizard
 * (`pages/subscriptions/.../editors/ScheduleEditor.tsx`).
 *
 * Renders a `Select` of days 1-31; 29/30/31 carry an info icon whose tooltip
 * warns that those dates are skipped in shorter months, plus a matching help
 * line below the field when such a day is selected.
 *
 * Forwards `ref`, `id`, and the `aria-*` props onto the underlying
 * `SelectTrigger` so it can sit inside `<FormControl>` — Slot then wires up
 * label/error binding via `useFormField`.
 */
const DayOfMonthSelect = forwardRef<ElementRef<typeof SelectTrigger>, DayOfMonthSelectProps>(
  ({ value, onValueChange, className, ...triggerProps }, ref) => (
    <>
      <Select value={value} onValueChange={onValueChange}>
        {/* `id` is forwarded — via FormControl/Slot in the Studio dialog or
            explicitly from the Subscriptions wizard — so the sibling
            `<FormLabel htmlFor>` / `<Label htmlFor>` provides the accessible
            name. A trigger-level `aria-label` would override that label per
            WAI-ARIA name computation, so we deliberately don't set one. */}
        <SelectTrigger ref={ref} className={className} {...triggerProps}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DAY_OF_MONTH_OPTIONS.map(day => (
            <SelectItem key={day} value={day}>
              <Stack tag="span" direction="row" align="center" gap="xs">
                {day}
                {SHORT_MONTH_DAYS.has(day) && (
                  <Tooltip
                    title={SHORT_MONTH_TOOLTIP}
                    contentProps={{ style: { zIndex: TOOLTIP_Z_INDEX } }}
                  >
                    {/* role + aria-label so screen readers announce the
                        warning as part of the option's accessible name —
                        the bare <span> trigger isn't keyboard-focusable. */}
                    <span className="al-inline-flex" role="img" aria-label={SHORT_MONTH_TOOLTIP}>
                      <IconInfoCircle size={14} className="al-text-muted-foreground" />
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {SHORT_MONTH_DAYS.has(value) && (
        <p className="al-text-xs al-text-muted-foreground al-mt-1">
          Day 29, 30, or 31 only runs in months that contain that date; other months are skipped.
        </p>
      )}
    </>
  )
);
DayOfMonthSelect.displayName = "DayOfMonthSelect";

export default DayOfMonthSelect;
